import { assert_false, assert_not_null, assert_type_array } from '@shared/asserts/utils_asserts';
import { DTO__base } from '@shared/common/DTO__base';
import { CONSTANTS__shared } from '@shared/constants/CONSTANTS__shared';
import { FN_DTO_BASE__try_assert_standard_fields } from '@shared/network/_methods/FN_DTO_BASE__try_assert_standard_fields';
import { ENUM__dto_class_name } from '@shared/serialization/ENUM__dto_class_name';
import { cloneDeep } from 'es-toolkit';

export class MAP__serializable<TKey extends string | number | symbol, TValue> extends DTO__base {
  /*
   * STATIC FIELDS
   */
  static readonly _class_name_static: string = ENUM__dto_class_name.MAP_SERIALIZABLE;
  /*
   * STATIC METHODS
   */

  static from_map<TKey extends string | number | symbol, TValue>(source: Map<TKey, TValue>): MAP__serializable<TKey, TValue> {
    let out = new MAP__serializable<TKey, TValue>();

    for (let [key, value] of source.entries()) {
      out.set(key as TKey, value as TValue);
    }

    return out;
  }

  static from_object<TKey extends string | number | symbol, TValue>(source: Record<TKey, TValue>): MAP__serializable<TKey, TValue> {
    let out = new MAP__serializable<TKey, TValue>();

    for (let [key, value] of Object.entries(source)) {
      out.set(key as TKey, value as TValue);
    }

    return out;
  }

  /*
   * FIELDS
   */

  _items_holder: any;

  /*
   * METHODS
   */
  constructor() {
    super();

    this.clear();
  }

  /*
   * GETTERS
   */

  keys(): string[] {
    return Object.keys(this.to_object());
  }

  values(): TValue[] {
    return Object.values(this.to_object());
  }

  entries(): [string, TValue][] {
    return Object.entries(this.to_object());
  }

  has(key: TKey) {
    return key in this._items_holder;
  }

  size() {
    let items_without_class_name = { ...this._items_holder };

    delete items_without_class_name[CONSTANTS__shared.SERIALIZATION_CLASS_FIELD_INSTANCE];

    return Object.keys(items_without_class_name).length;
  }

  is_empty() {
    return this.size() == 0;
  }

  /*
   * GET VALUES
   */

  get(key: TKey): TValue | undefined {
    return this._items_holder[key];
  }

  get_or_default(key: TKey, default_value: TValue): TValue {
    let out = this._items_holder[key];

    if (out === undefined) {
      out = default_value;
    }

    return out;
  }

  get_or_assert(key: TKey): TValue {
    let out = this._items_holder[key];

    assert_not_null(out, { key });

    return out;
  }

  get_or_assert_typed<T>(key: TKey): T {
    let out = this._items_holder[key];

    assert_not_null(out, { key });

    return out;
  }

  get_or_create_map(key: TKey) {
    if (this.has(key)) return this.get_or_assert(key);

    let out = new MAP__serializable() as TValue;

    this.set(key, out);

    return out;
  }

  get_or_create_array(key: TKey) {
    if (this.has(key)) return this.get_or_assert(key);

    let out = [] as TValue;

    this.set(key, out);

    return out;
  }

  add_item_to_array(key: TKey, value: TValue extends any[] ? TValue[number] : never) {
    let target_array = this.get_or_create_array(key);

    assert_type_array(target_array);

    target_array.push(value);
  }

  /*
   * ACTIONS
   */

  set(key: TKey, value: TValue) {
    this._items_holder[key] = value;
  }

  delete(key: TKey) {
    delete this._items_holder[key];
  }

  clear() {
    this._items_holder = {};
    this._items_holder[CONSTANTS__shared.SERIALIZATION_CLASS_FIELD_INSTANCE] = ENUM__dto_class_name.MAP_SERIALIZABLE_ITEMS_HOLDER;
  }

  /*
   * CONVERTIONS
   */

  to_object(): Record<string, any> {
    let out: Record<string, any> = {};

    for (let key of Object.keys(this._items_holder)) {
      if (key == CONSTANTS__shared.SERIALIZATION_CLASS_FIELD_INSTANCE) continue;

      let value = this._items_holder[key];

      if (value instanceof MAP__serializable) {
        value = value.to_object();
      } else if (Array.isArray(value)) {
        if (value.is_empty()) {
          value = [];
        } else {
          let item_1st = value[0];

          if (item_1st instanceof MAP__serializable) {
            value = value.map(item => item.to_object());
          } else {
            value = [...value];
          }
        }
      }

      out[key] = value;
    }

    return out;
  }

  from_object(object: Record<string, any>) {
    this._items_holder = cloneDeep(object);
    this._items_holder[CONSTANTS__shared.SERIALIZATION_CLASS_FIELD_INSTANCE] = ENUM__dto_class_name.MAP_SERIALIZABLE_ITEMS_HOLDER;
  }

  to_map(): Map<TKey, TValue> {
    const out = new Map<TKey, TValue>();

    for (const [key, value] of Object.entries(this._items_holder)) {
      if (key === CONSTANTS__shared.SERIALIZATION_CLASS_FIELD_INSTANCE) continue;
      out.set(key as unknown as TKey, value as TValue);
    }

    return out;
  }

  toString() {
    return `size: ${this.size()}`;
  }

  merge_with(other: MAP__serializable<TKey, TValue>): void {
    for (let [key, value] of other.entries()) {
      this.set(key as unknown as TKey, value);
    }
  }

  /*
   * ASSERTS
   */
  assert_not_exists(key: TKey) {
    assert_false(this.has(key));
  }

  assert_standard_fields(): void {
    for (let value of this.values()) {
      FN_DTO_BASE__try_assert_standard_fields(value);
    }
  }
}
